import { Button, ChevronLeftIcon, Drawer, Flex, Menu, Text } from 'components'
import { useWorkspace } from 'components/providers'
import { isEmpty } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Lead, PrimaryLeadProperties, PrimaryLeadProperty, primaryLeadProperties } from 'types'
import { readSpreadsheet, sheetToJson, uploadFile } from 'utils'
import { useEvent } from '../../../utils/eventUtils'
import PropertyMapper, { defaultPrimaryProperties } from '../../PropertyMapper'

interface Props {
  open: boolean
  onClose: () => void
  fileBuffer?: ArrayBuffer
}

export default function DatabaseMappingDrawer({ open, onClose, fileBuffer }: Props) {
  const { event } = useEvent()
  const { workspace } = useWorkspace()
  const [dbTabs, setDbTabs] = useState<string[]>([])
  const [selectedTab, setSelectedTab] = useState('')
  const [rawDbData, setRawDbData] = useState<Record<string, string>[]>()
  const [dbFields, setDbFields] = useState<Record<string, string>>()
  const [leadMapping, setLeadMapping] = useState<PrimaryLeadProperties>()
  const dbFilePath = `/events/${event?.id}/attendees.json`

  useEffect(() => {
    if (!fileBuffer) return
    ;(async () => {
      const { SheetNames } = await readSpreadsheet(fileBuffer)
      setDbTabs(SheetNames)
      if (SheetNames.length == 1) setSelectedTab(SheetNames[0])
    })()
  }, [fileBuffer])

  useEffect(() => {
    if (!selectedTab) return
    processFile()
  }, [selectedTab])

  async function processFile() {
    if (!fileBuffer) return

    const { Sheets } = await readSpreadsheet(fileBuffer)
    const sheet = Sheets[selectedTab]
    const data = await sheetToJson(sheet)
    setRawDbData(data)
    setDbFields(data[0])
  }

  async function saveMapping() {
    if (!rawDbData || !leadMapping) return

    const formatted = rawDbData.map(row => {
      return Object.entries(row).reduce((acc, [key, value]) => {
        const mappedKey = Object.keys(leadMapping).find(
          mappedFieldKey => leadMapping[mappedFieldKey as PrimaryLeadProperty] === key
        )

        if (mappedKey) {
          return { ...acc, [mappedKey]: value }
        } else {
          return {
            ...acc,
            custom: { ...(acc.custom || {}), [key]: value as string },
          }
        }
      }, {} as Lead)
    })

    const jsonFile = new File([JSON.stringify(formatted)], 'attendees.json', {
      type: 'application/json',
    })

    try {
      await uploadFile(`Workspaces/${workspace!.id}${dbFilePath}`, jsonFile)
      handleClose()
    } catch (error) {
      console.error('File upload failed:', error)
    }
  }

  function handleClose() {
    onClose()
    setDbFields(undefined)
    setDbTabs([])
    setSelectedTab('')
    setRawDbData(undefined)
  }

  return (
    <Drawer
      onClose={handleClose}
      open={open}
      size="lg"
      header={
        <Button variant="link" leftIcon={<ChevronLeftIcon />} onClick={handleClose} px={0}>
          Back
        </Button>
      }
      footer={
        <Button palette="primary" disabled={isEmpty(leadMapping)} onClick={saveMapping}>
          Save
        </Button>
      }
    >
      {dbTabs && (
        <Flex gap={5} align="center" py={2}>
          <Text textStyle="h6">Database tab:</Text>
          <Menu
            variant="outline"
            borderRadius="10px"
            menuProps={{ positioning: { sameWidth: true } }}
            value={selectedTab}
            options={dbTabs.map(tab => ({ label: tab, value: tab }))}
            onChange={value => setSelectedTab(value)}
          />
        </Flex>
      )}
      {dbFields && (
        <PropertyMapper
          defaultProperties={defaultPrimaryProperties}
          inputFieldLabel="Database Fields"
          inputMap={dbFields}
          onChange={setLeadMapping}
          properties={primaryLeadProperties}
          py={2}
        />
      )}
    </Drawer>
  )
}
